import { useEffect, useRef, useState } from "react";
import { getSkyStars } from "@/lib/api";
import { useRole } from "@/contexts/RoleContext";

// ── Deterministic pseudo-random (seeded by index) ──────────────────────
function seeded(n) {
  const x = Math.sin(n + 1.7) * 43758.5453123;
  return x - Math.floor(x);
}

// Each star gets a stable position based on its index in the array.
function starPos(idx) {
  return {
    left: `${(seeded(idx * 3 + 0) * 86 + 5).toFixed(2)}%`,
    top:  `${(seeded(idx * 3 + 1) * 76 + 8).toFixed(2)}%`,
  };
}

// ── Tier config ─────────────────────────────────────────────────────────
const TIER = {
  soft: {
    size: 4,
    color: "rgba(186, 214, 255, 0.60)",
    shadow: "0 0 5px rgba(186,214,255,0.45)",
    dur: "3.8s",
  },
  regular: {
    size: 7,
    color: "rgba(255, 240, 140, 0.92)",
    shadow: "0 0 8px rgba(255,240,140,0.7), 0 0 18px rgba(255,240,140,0.3)",
    dur: "2.9s",
  },
  bright: {
    size: 12,
    color: "#FFD700",
    shadow: "0 0 12px #FFD700, 0 0 26px rgba(255,215,0,0.55), 0 0 42px rgba(255,195,0,0.22)",
    dur: "2.2s",
  },
  bright_heavy: {
    size: 19,
    color: "#FFFDE0",
    shadow: "0 0 16px #FFD700, 0 0 34px rgba(255,215,0,0.8), 0 0 60px rgba(255,160,0,0.55), 0 0 90px rgba(255,120,0,0.25)",
    dur: "1.7s",
  },
};

// Placeholder stars — always shown so sky is never blank (empty state)
const PLACEHOLDERS = [
  { left: "12%",  top: "28%" },
  { left: "63%",  top: "52%" },
  { left: "84%",  top: "19%" },
];

// Stars added within this window get the sparkle-in entrance animation
const RECENT_MS = 15 * 60 * 1000; // 15 minutes

export default function YourSky({ className = "" }) {
  const { learner } = useRole();
  const [stars, setStars] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const nowRef = useRef(Date.now());

  useEffect(() => {
    if (!learner?.learner_id) return;
    getSkyStars(learner.learner_id)
      .then(d => {
        setStars(d.stars || []);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [learner?.learner_id]);

  return (
    <div
      className={`your-sky-window ${className}`}
      data-testid="your-sky"
      aria-label="Your Sky"
      role="img"
    >
      {/* Label */}
      <span className="your-sky-label">Your Sky</span>

      {/* Nebula overlay for depth */}
      <div className="your-sky-nebula" aria-hidden="true" />

      {/* Always-visible placeholder stars (empty-state guarantee) */}
      {PLACEHOLDERS.map((pos, i) => (
        <div
          key={`ph-${i}`}
          className="sky-star sky-star--placeholder"
          aria-hidden="true"
          style={{
            left: pos.left,
            top: pos.top,
            animationDelay: `${i * 1.1}s`,
          }}
        />
      ))}

      {/* Real stars derived from Mitraa moments */}
      {loaded && stars.map((star, idx) => {
        const cfg = TIER[star.tier] || TIER.soft;
        const pos = starPos(idx);
        const isNew = star.timestamp
          && (nowRef.current - new Date(star.timestamp).getTime()) < RECENT_MS;
        return (
          <div
            key={`s-${idx}`}
            className={`sky-star sky-star--${star.tier}${isNew ? " sky-star--new" : ""}`}
            data-testid={`sky-star-${star.tier}`}
            data-moment-id={star.moment_id}
            aria-hidden="true"
            style={{
              left: pos.left,
              top:  pos.top,
              width:  cfg.size,
              height: cfg.size,
              background: cfg.color,
              boxShadow: cfg.shadow,
              animationDuration: cfg.dur,
              animationDelay: `${(seeded(idx * 7 + 3) * 2.4).toFixed(2)}s`,
            }}
          />
        );
      })}

      {/* Whisper tagline */}
      <span className="your-sky-whisper" aria-hidden="true">
        Your sky grows with you — one star at a time.
      </span>

      {/* bright_heavy cross-spike overlay */}
      {loaded && stars
        .map((star, idx) => star.tier === "bright_heavy" ? idx : null)
        .filter(idx => idx !== null)
        .map(idx => {
          const pos = starPos(idx);
          return (
            <div
              key={`spike-${idx}`}
              className="sky-star-spike"
              aria-hidden="true"
              style={{ left: pos.left, top: pos.top }}
            />
          );
        })
      }
    </div>
  );
}
